import dynamic from 'next/dynamic';

import * as React from 'react';

import { Slot, Slottable } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import type { IconName } from '@/shared/ui/icon/icon';

const Icon = dynamic(() => import('@/shared/ui/icon/icon').then(i => i.Icon), { ssr: true });

const buttonIconBaseClasses = `
    group
    isolate
    inline-flex items-center justify-center
    align-middle
    overflow-hidden
    rounded-full
    text-button-md-500
    cursor-pointer
    select-none
    outline-none

    disabled:bg-surface-disabled
    disabled:text-black/20
    disabled:pointer-events-none
    disabled:cursor-default
    focus-visible:ring-4
    focus-visible:ring-stroke-focus
    transition-[opacity,transform,background-color,box-shadow,border-color] duration-300
`;

const buttonIconVariants = cva(buttonIconBaseClasses, {
    variants: {
        variant: {
            floating:
                'bg-white text-black disabled:shadow-none shadow-[0_8px_16px_0_rgba(0,0,0,0.20)] hover:bg-[linear-gradient(0deg,rgba(0,0,0,0.05)_0%,rgba(0,0,0,0.05)_100%)] active:bg-[linear-gradient(0deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0)_100%)]',
            primary:
                'bg-surface-gray-light text-black hover:border-stroke-light border border-transparent active:bg-surface-disabled',
            primaryDark: 'bg-surface-white-light text-white disabled:bg-surface-white-disabled disabled:text-white/30',
            secondary:
                'bg-transparent text-black border-stroke-light border hover:border-stroke active:border-stroke-light disabled:border-stroke-disabled disabled:bg-transparent',
            secondaryDark:
                'bg-transparent text-white border-stroke-inverse-light border hover:border-stroke-inverse active:border-stroke-inverse-light disabled:border-stroke-inverse-disabled disabled:bg-transparent disabled:text-white/30',
        },
        size: {
            large: 'size-10 md:size-11',
            small: 'size-6 md:size-7',
            menu: 'size-6',
        },
    },
    defaultVariants: {
        variant: 'primary',
        size: 'large',
    },
});

interface ButtonIconProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonIconVariants> {
    asChild?: boolean;
    icon?: IconName;
    as?: React.ElementType;
}

const ButtonIcon = React.forwardRef<HTMLButtonElement, ButtonIconProps>(
    ({ className, variant, size, icon = 'IcArrow', asChild = false, as: Element = 'button', ...props }, ref) => {
        const BtnIcon = () => (
            <Icon
                name={icon}
                className={cn({
                    'size-5 min-w-5 md:size-6 md:min-w-6': size === 'large',
                    'size-4 min-w-4': size === 'small',
                })}
            />
        );

        const Comp = asChild ? Slot : Element;

        return (
            <Comp
                className={cn(buttonIconVariants({ variant, size, className }))}
                ref={ref}
                {...(Comp === 'button' ? { type: 'button' } : {})}
                {...props}>
                <span className='sr-only'>
                    <Slottable>{props.children}</Slottable>
                </span>
                <BtnIcon />
            </Comp>
        );
    },
);
ButtonIcon.displayName = 'ButtonIcon';

export { ButtonIcon, buttonIconVariants };
